import { tierMarkColor, tierMeta } from '../Benchmark/BenchmarkVocabulary';
import { useThemeTokens } from '../../theme/ThemeModeProvider';
import { ThemeName } from '../../theme/tokens';

/**
 * Chart colour for the case-workflow dashboard.
 *
 * Every palette here was validated with the dataviz validator against both
 * chart surfaces (#112240 dark, #FFFFFF light). The results are recorded beside
 * each one so the next person changing a colour knows what has to still hold.
 */

/**
 * Surface, grid and axis text for the theme currently painted. Hex rather than
 * CSS variables: recharts writes them into SVG attributes.
 */
export const useChartChrome = () => {
  const tokens = useThemeTokens();
  return { surface: tokens.surface, grid: tokens.line, axisText: tokens['fg-2'] };
};

/**
 * Magnitude bars: ONE hue.
 *
 * A breakdown bar chart labels every bar with its own category, so colour
 * carries no identity there and a different hue per bar would be the rainbow
 * anti-pattern — it implies a categorical difference the chart is not encoding.
 */
export const MAGNITUDE_HUE = '#3B82F6';

/**
 * Categorical series, in fixed order — assigned by position, never cycled.
 *
 * Validated dark / surface #112240, and again light / surface #FFFFFF:
 *   lightness band PASS (all inside L 0.48-0.67) · chroma floor PASS
 *   CVD separation PASS (worst adjacent #0D9488-#D97706 ΔE 12.5 protan)
 *   normal-vision PASS (worst ΔE 20.3) · contrast PASS (all >= 3:1)
 *
 * Colour follows the entity, not its rank: SERIES_COLOURS is indexed by a
 * stable ordering of the dimension's values, so filtering one series out must
 * not repaint the survivors.
 */
export const SERIES_COLOURS = [
  '#3B82F6',
  '#DB2777',
  '#D97706',
  '#0D9488',
  '#8B5CF6',
  '#65A30D',
  '#0891B2'
];

/** Anything past the 7th series, and every explicitly-unknown bucket. */
export const OTHER_COLOUR = '#64748B';

/** A named null bucket is grey on purpose: it is an absence, not a category. */
export const NULL_BUCKET_COLOUR = '#64748B';

/**
 * A ninth series is never a generated hue.
 *
 * Past the fixed order, values fold into "Other" rather than being given a
 * colour the palette was never validated for.
 */
export const seriesColour = (index: number): string =>
  index < SERIES_COLOURS.length ? SERIES_COLOURS[index] : OTHER_COLOUR;

export const MAX_SERIES = SERIES_COLOURS.length;

/**
 * Priority tiers — a semantic-heat scale, cool to hot.
 *
 * This is deliberately NOT a fresh palette. `BenchmarkVocabulary.tierMeta`
 * already owns P5-P2 and `TierPill` already renders them on the benchmark
 * screen; a second set of tier colours here would mean "P4" is orange on one
 * screen and something else on another, which is exactly the drift that
 * vocabulary file was written to stop.
 *
 * It fails the single-hue ordinal check by construction — hue spread 108° —
 * and that is the sanctioned exception: semantic heat, always shown with
 * direct labels and a legend so identity never rests on colour alone. P1 is
 * added here because the benchmark scale stops at P2; indigo extends the cool
 * end, keeps every adjacent lightness gap >= 0.06, and clears the surface at
 * 3.54:1.
 */
const P1_COLOUR = '#6366F1';

/** Least urgent first, so a chart reads cool-to-hot left to right. */
export const PRIORITY_ORDER = ['P1', 'P2', 'P3', 'P4', 'P5'];

export const priorityColour = (code: string, theme: ThemeName = 'dark'): string => {
  if (code === 'P1') return P1_COLOUR;
  // Both fall back to grey for anything they do not know, which is the right
  // answer for 'never_assigned' and 'unassigned'. On white the tiers take their
  // mark colours (see tierMarkColor); P1 clears white at 4.5:1 as it is.
  return tierMarkColor(code, theme);
};

export const priorityLabel = (code: string): string => {
  if (code === 'P1') return 'Lowest (P1)';
  const meta = tierMeta(code);
  return meta.label === code ? code : `${meta.label} (${meta.code})`;
};

/** Sorts tier buckets cool-to-hot, with named non-tier buckets pinned last. */
export const byPriority = (a: string, b: string): number => {
  const ai = PRIORITY_ORDER.indexOf(a);
  const bi = PRIORITY_ORDER.indexOf(b);
  if (ai === -1 && bi === -1) return a.localeCompare(b);
  if (ai === -1) return 1;
  if (bi === -1) return -1;
  return ai - bi;
};
